import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash, Minus, Plus, WhatsappLogo, PaypalLogo } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { whatsappLink } from "@/data/site";
import { fbqTrack } from "@/lib/track";
import { useProducts } from "@/lib/productsStore";
import { ProductImg } from "@/components/ProductImg";

const mxn = (n) => `$${n.toLocaleString("es-MX")} MXN`;

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const { products } = useProducts();
  const navigate = useNavigate();
  const agotado = (id) => {
    const p = products.find((x) => x.id === id);
    return p && p.disponible === false;
  };
  const hasAgotado = items.some((i) => agotado(i.id));

  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    const b = document.body.style;
    b.position = "fixed";
    b.top = `-${y}px`;
    b.left = "0";
    b.right = "0";
    b.width = "100%";
    return () => {
      b.position = "";
      b.top = "";
      b.left = "";
      b.right = "";
      b.width = "";
      window.scrollTo(0, y);
    };
  }, [open]);

  const orderMessage = () => {
    const lines = items.map((i) => `• ${i.qty} × ${i.name} | ${mxn(i.price_mxn * i.qty)}`);
    return `Hola Ruta 360, quiero hacer este pedido:\n\n${lines.join("\n")}\n\nTotal: ${mxn(total)}`;
  };

  return (
    <>
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/50 z-[1090]"
          />
          <motion.aside
            data-testid="cart-drawer"
            data-lenis-prevent
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full max-w-[100vw] sm:max-w-md bg-white z-[1100] flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-black/10 bg-black text-white">
              <span className="font-display text-xl uppercase font-semibold tracking-wide">Tu pedido</span>
              <button data-testid="close-cart-button" onClick={() => setOpen(false)} aria-label="Cerrar carrito" className="p-2 hover:text-[#FF5508] transition-colors">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">
              {items.length === 0 ? (
              <p data-testid="cart-empty-message" className="text-black/50 text-sm text-center mt-16">
                  Tu carrito está vacío
                </p>
              ) : (
                <ul className="flex flex-col gap-5">
                  {items.map((i) => (
                    <li key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 border border-black/10 p-3">
                      <div className="w-20 h-20 bg-[#F4F5F6] shrink-0 overflow-hidden">
                        <ProductImg src={i.image} alt={i.name} w={400} className={`w-full h-full ${i.image.startsWith("/images/") ? "object-contain p-1" : "object-cover"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-[#111418] leading-snug truncate">{i.name}</p>
                        {agotado(i.id) && (
                          <p data-testid={`cart-agotado-${i.id}`} className="text-[#DC2626] text-xs font-semibold mt-0.5">Agotado</p>
                        )}
                        <p className="font-bold text-sm mt-1">{mxn(i.price_mxn)}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button data-testid={`cart-minus-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="w-7 h-7 rounded-full border border-black/15 flex items-center justify-center hover:border-black transition-colors" aria-label="Quitar uno">
                            <Minus size={13} />
                          </button>
                          <span className="text-sm w-5 text-center">{i.qty}</span>
                          <button data-testid={`cart-plus-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="w-7 h-7 rounded-full border border-black/15 flex items-center justify-center hover:border-black transition-colors" aria-label="Agregar uno">
                            <Plus size={13} />
                          </button>
                          <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="ml-auto text-black/40 hover:text-[#FF5508] transition-colors" aria-label="Eliminar">
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-black/10 px-6 py-5">
                <div data-testid="free-shipping-progress" className="mb-4">
                  <p className="text-xs font-semibold text-black/70 mb-2">
                    {total >= 1399
                      ? "¡Tienes ENVÍO GRATIS!"
                      : `Te faltan ${mxn(1399 - total)} para tener ENVÍO GRATIS`}
                  </p>
                  <div className="h-2 rounded-full bg-black/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${total >= 1399 ? "bg-[#25D366]" : "bg-[#FF5508]"}`}
                      style={{ width: `${Math.min(100, (total / 1399) * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-black/50">Envío</span>
                  <span data-testid="cart-envio" className={`text-sm font-semibold ${total >= 1399 ? "text-[#25D366]" : "text-[#111418]"}`}>
                    {total >= 1399 ? "Gratis" : mxn(99)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-black/50">Total</span>
                  <span data-testid="cart-total" className="font-bold text-2xl text-[#111418]">{mxn(total >= 1399 ? total : total + 99)}</span>
                </div>
                <p data-testid="cart-shipping-note" className="text-black/45 text-xs mb-4">
                  Envío a todo México: $99. Gratis en compras desde $1,399 MXN.
                </p>
                {hasAgotado ? (
                  <p data-testid="cart-agotado-warning" className="mb-3 text-xs font-semibold text-[#DC2626] bg-[#DC2626]/10 rounded-xl px-4 py-3">
                    Quita los productos agotados para continuar con el pago.
                  </p>
                ) : (
                  <>
                    <button
                      data-testid="cart-mercadopago-button"
                      onClick={() => {
                        setOpen(false);
                        navigate("/checkout");
                      }}
                      className="w-full h-12 rounded-full bg-[#009EE3] text-white text-sm font-semibold mb-3 transition-colors duration-300 hover:bg-black"
                    >
                      Pagar con Mercado Pago
                    </button>
                    <button
                      data-testid="cart-paypal-button"
                      onClick={() => {
                        setOpen(false);
                        navigate("/checkout");
                      }}
                      className="w-full h-12 rounded-full bg-[#FFC439] text-[#111418] text-sm font-semibold inline-flex items-center justify-center gap-2 mb-3 transition-colors duration-300 hover:bg-[#f0b429]"
                    >
                      <PaypalLogo size={18} weight="fill" />
                      Pagar con PayPal
                    </button>
                  </>
                )}
                <a
                  data-testid="cart-checkout-whatsapp"
                  onClick={() => fbqTrack("InitiateCheckout", { value: total, currency: "MXN" })}
                  href={whatsappLink(orderMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#25D366] text-white text-xs px-6 py-4 rounded-full transition-colors duration-300 hover:bg-black"
                >
                  <WhatsappLogo size={18} weight="fill" />
                  Pedir por WhatsApp
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
    </>
  );
};
